import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { pdf } from '@react-pdf/renderer';
import { useMutation, useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { treatmentApi } from 'src/api/treatment';
import { visitScopedPrescribingApi } from 'src/api/visit-scoped-prescribing';
import Iconify from 'src/components/iconify';
import {
  TreatmentPdfItem,
  TreatmentsSummaryPDF,
} from 'src/components/treatments-summary-pdf';
import ProfileTreatmentFormModal, {
  TreatmentFormSubmitValues,
} from 'src/sections/@dashboard/profile/components/ProfileTreatments/ProfileTreatmentFormModal';
import type { TreatmentFormSnapshot } from 'src/sections/@dashboard/profile/components/ProfileTreatments/treatmentFormValues';
import { useTreatmentEditSeries } from 'src/sections/@dashboard/visit/hooks/useTreatmentWriteSeries';
import { patientOrVisitRoute } from 'src/sections/@dashboard/warehouse/hooks/useClinicalWriteSeries';
import { useStockWriteError } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteError';
import { useStockWriteFeedback } from 'src/sections/@dashboard/warehouse/hooks/useStockWriteFeedback';
import {
  GetProfileTreatmentDto,
  UpdateProfileTreatmentDto,
} from 'woofwoof-api';
import { useVisitModalAction } from './VisitModalActionContext';
import VisitModalEmptyState from './VisitModalEmptyState';
import { translateKey } from 'src/locales/translate';


interface VisitModalTreatmentsTabProps {
  treatments: GetProfileTreatmentDto[];
  canEdit: boolean;
  canExportPdf: boolean;
  canSeePrices: boolean;
  visitId: string;
  patientId: string;
  organizationId?: string;
  organizationName: string;
  visitStartTime: string;
  onActivityChange: () => void;
}

export default function VisitModalTreatmentsTab({
  treatments,
  canEdit,
  canExportPdf,
  canSeePrices,
  visitId,
  patientId,
  organizationId,
  organizationName,
  visitStartTime,
  onActivityChange,
}: VisitModalTreatmentsTabProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const writeError = useStockWriteError();
  const onStockWrite = useStockWriteFeedback();
  const { setAction } = useVisitModalAction();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTreatmentId, setEditingTreatmentId] = useState<string | null>(
    null,
  );

  const editingTreatment = editingTreatmentId
    ? treatments.find((t) => t.id === editingTreatmentId)
    : undefined;

  const { data: catalogData } = useQuery(
    ['treatment-catalog-modal', organizationId],
    () =>
      treatmentApi.treatmentControllerGetCatalog({
        organizationId: organizationId!,
      }),
    { enabled: canEdit && Boolean(organizationId) },
  );

  const isGuestVisit = !patientId;

  // Adding treatments now goes through the global "Dodaj do wizyty" menu; this
  // tab only edits / deletes existing ones (modal opens via row click) and
  // exports selected rows to PDF.
  // R3-4: an edit series through the patient, or the visit on a guest visit.
  const treatmentSeries = useTreatmentEditSeries({
    route: patientOrVisitRoute(patientId, visitId),
    editingId: editingTreatmentId ?? '',
    onSuccess: () => {
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseAddSuccess'), { variant: 'success' });
      setIsModalOpen(false);
      setEditingTreatmentId(null);
    },
    onError: writeError(translateKey('snackbar.treatmentUpdateError')),
  });

  const { mutate: deleteTreatment, isLoading: isDeleting } = useMutation({
    mutationFn: (treatmentId: string) =>
      isGuestVisit
        ? visitScopedPrescribingApi.deleteTreatment(visitId, treatmentId)
        : treatmentApi.treatmentRecordControllerDeleteProfileTreatment({
            profileId: patientId,
            treatmentId,
            visitId,
          }),
    // A delete reverses the treatment's stock.
    onSuccess: (response) => {
      onStockWrite(response);
      onActivityChange();
      enqueueSnackbar(t('visit.visitCourseRemoveSuccess'), {
        variant: 'success',
      });
      setIsModalOpen(false);
      setEditingTreatmentId(null);
    },
    // M-1: a refused delete says why.
    onError: writeError(translateKey('snackbar.treatmentDeleteError')),
  });

  const handleSubmit = (
    values: TreatmentFormSubmitValues,
    shown: TreatmentFormSnapshot,
  ) => {
    // An edit names no clinic: `UpdateProfileTreatmentDto` has no such field.
    treatmentSeries.submit(
      editingTreatmentId ?? '',
      values as UpdateProfileTreatmentDto,
      shown,
    );
  };

  const handleOpenEdit = (id: string) => {
    setEditingTreatmentId(id);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingTreatmentId(null);
  };

  const handleToggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(treatments.map((t) => t.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleExportPdf = async () => {
    setIsExporting(true);
    try {
      const items: TreatmentPdfItem[] = treatments
        .filter((t) => selectedIds.has(t.id))
        .map((t) => ({
          name: t.name,
          treatmentDate: t.treatmentDate,
          priceNetto: t.priceNetto,
          vatRate: t.vatRate,
          priceBrutto: t.priceBrutto,
        }));

      const blob = await pdf(
        <TreatmentsSummaryPDF
          treatments={items}
          showPatientColumn={false}
          organizationName={organizationName}
        />,
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `zabiegi-wizyta-${format(
        new Date(visitStartTime),
        'dd-MM-yyyy',
      )}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      enqueueSnackbar(translateKey("hardcoded.nie_udalo_sie_wygenerowac_pdf"), { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const actions: JSX.Element[] = [];

    if (canExportPdf && selectedIds.size > 0) {
      actions.push(
        <LoadingButton
          key="pdf"
          size="medium"
          variant="contained"
          startIcon={<Iconify icon="eva:download-outline" width={18} />}
          loading={isExporting}
          onClick={handleExportPdf}
          sx={{
            background: theme.palette.common.black,
            color: theme.palette.common.white,
            textTransform: 'none',
          }}
        >
          Generuj PDF ({selectedIds.size})
        </LoadingButton>,
      );
    }

    if (actions.length > 0) {
      setAction(
        <Stack direction="row" spacing={1}>
          {actions}
        </Stack>,
      );
    } else {
      setAction(null);
    }

    return () => setAction(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canExportPdf, selectedIds.size, isExporting, t, setAction]);

  if (treatments.length === 0 && !canEdit) {
    return <VisitModalEmptyState message={translateKey("hardcoded.brak_zabiegow_przypisanych_podczas_tej_wizyty")} />;
  }

  return (
    <>
      <Stack sx={{ gap: 2, flex: 1, minHeight: 0 }}>
        {treatments.length === 0 ? (
          <VisitModalEmptyState message={translateKey("hardcoded.brak_zabiegow_przypisanych_podczas_tej_wizyty")} />
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {canExportPdf && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          selectedIds.size === treatments.length &&
                          treatments.length > 0
                        }
                        indeterminate={
                          selectedIds.size > 0 &&
                          selectedIds.size < treatments.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                  )}
                  <TableCell>Nazwa</TableCell>
                  <TableCell>{translateKey("visit.tableDate")}</TableCell>
                  {canSeePrices && (
                    <>
                      <TableCell align="right">Cena netto</TableCell>
                      <TableCell align="right">VAT</TableCell>
                      <TableCell align="right">Cena brutto</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {treatments.map((tr) => (
                  <TableRow
                    key={tr.id}
                    hover={canEdit}
                    onClick={canEdit ? () => handleOpenEdit(tr.id) : undefined}
                    sx={canEdit ? { cursor: 'pointer' } : undefined}
                  >
                    {canExportPdf && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedIds.has(tr.id)}
                          onChange={() => handleToggle(tr.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {tr.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {tr.treatmentDate
                          ? format(new Date(tr.treatmentDate), 'dd.MM.yyyy')
                          : '-'}
                      </Typography>
                    </TableCell>
                    {canSeePrices && (
                      <>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {tr.priceNetto != null
                              ? `${tr.priceNetto.toFixed(2)} ${translateKey("hardcoded.currencyPln")}`
                              : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {tr.vatRate != null ? `${tr.vatRate}%` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={500}>
                            {tr.priceBrutto != null
                              ? `${tr.priceBrutto.toFixed(2)} ${translateKey("hardcoded.currencyPln")}`
                              : '-'}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      <ProfileTreatmentFormModal
        isModalOpen={isModalOpen}
        closeModal={handleClose}
        treatment={editingTreatment as any}
        onSubmit={handleSubmit}
        stockEnabled
        unconfirmedSave={treatmentSeries.pending}
        unconfirmedBlocked={treatmentSeries.blocked}
        unconfirmedValues={treatmentSeries.frozenValues}
        onDelete={
          editingTreatmentId
            ? () => deleteTreatment(editingTreatmentId)
            : () => {}
        }
        isProfessional
        organizationId={organizationId}
        catalogOptions={(catalogData?.data as any) ?? []}
        isCreating={false}
        isUpdating={treatmentSeries.isLoading}
        isDeleting={isDeleting}
        singleReminderOnly={isGuestVisit}
      />
    </>
  );
}
